import { useTranslation } from '@helios/frontend-core';
import { Fit, Layout, useRive, useViewModelInstanceNumber } from '@rive-app/react-canvas';
import { useEffect, useRef } from 'react';
import bottomNavRiv from '../../assets/navigation/palmetto_bottom_nav.riv';
import { useCollapseTrigger } from './useCollapseTrigger';

const STATE_MACHINE = 'State Machine';
const ARTWORK_INITIAL_ITEM_ID = 'home';

export const ARTBOARD_WIDTH_PX = 276;

const TAB_ORDER = ['home', 'energy', 'learn', 'shop'];

export type RiveBottomNavArtboard = 'Bottom Nav - Light' | 'Bottom Nav - Dark';

export interface RiveBottomNavProps {
  activeItemId: string;
  onNavClick: (id: string) => void;
  artboard: RiveBottomNavArtboard;
  isCollapsed?: boolean;
  /** Re-asserts `isCollapsed` on the Rive artwork whenever this changes - see `useCollapseTrigger`. */
  collapseResyncToken?: unknown;
  className?: string;
  onTabSettled?: (id: string) => void;
}

export const RiveBottomNav = ({
  activeItemId,
  onNavClick,
  artboard,
  isCollapsed = false,
  collapseResyncToken,
  className,
  onTabSettled,
}: RiveBottomNavProps) => {
  const { t } = useTranslation();

  const { rive, RiveComponent } = useRive(
    {
      src: bottomNavRiv,
      artboard,
      stateMachines: STATE_MACHINE,
      autoplay: true,
      autoBind: true,
      layout: new Layout({ fit: Fit.Contain }),
    },
    { shouldResizeCanvasToContainer: true },
  );

  const viewModelInstance = rive?.viewModelInstance ?? null;
  const { value: settledTab } = useViewModelInstanceNumber('settledTab', viewModelInstance);
  const triggeredItemIdRef = useRef(ARTWORK_INITIAL_ITEM_ID);

  useEffect(() => {
    if (!rive || !viewModelInstance || triggeredItemIdRef.current === activeItemId) {
      return;
    }
    triggeredItemIdRef.current = activeItemId;
    rive.pause();
    viewModelInstance.trigger(`${activeItemId}Selected`)?.trigger();
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        rive.play();
      });
    });
  }, [rive, viewModelInstance, activeItemId]);

  useEffect(() => {
    const settledItemId = settledTab === null ? undefined : TAB_ORDER[settledTab - 1];
    if (settledItemId && settledItemId === activeItemId) {
      onTabSettled?.(settledItemId);
    }
  }, [settledTab, activeItemId, onTabSettled]);

  useEffect(
    () => () => {
      rive?.cleanup();
    },
    [rive],
  );

  useCollapseTrigger(rive, isCollapsed, collapseResyncToken);

  return (
    <div className={`${className ?? ''} relative`}>
      <div style={{ pointerEvents: isCollapsed ? 'none' : undefined }} className="h-full w-full">
        <RiveComponent className="h-full w-full" />
      </div>
      <nav aria-label="Main navigation" className={`absolute inset-0 flex ${isCollapsed ? 'pointer-events-none' : ''}`}>
        {TAB_ORDER.map((id) => (
          <button
            key={id}
            type="button"
            role="link"
            tabIndex={isCollapsed ? -1 : undefined}
            aria-hidden={isCollapsed}
            aria-label={t(`navigation.${id}`)}
            aria-current={activeItemId === id ? 'page' : undefined}
            onClick={() => id !== activeItemId && onNavClick(id)}
            className="flex-1 h-full rounded-12 outline-none focus-visible:ring-2 focus-visible:ring-border-focus"
          />
        ))}
      </nav>
    </div>
  );
};
